import React, { useMemo, useState } from 'react';
import { TemplateType, TemplateTransportType } from '@frontend/app/types/globalTypes';
import { template } from 'lodash';
import { useProjectByIdQuery } from '@frontend/app/hooks';
import { useAuth } from '@frontend/context/authContext';
import { useGetDefaultMemberFieldSchemasQuery } from '@frontend/app/hooks/useGetDefaultMemberFieldSchemasQuery';
import { useSaveProgramMutation } from '@frontend/app/containers/Communities/AddOrEditCommunity/hooks/useSaveProgramMutation';
import { useMessagingContext } from '@frontend/hooks';
import { useGetMessageTemplates } from '@frontend/app/hooks';
import { logger } from '@common';
import { DEFAULT_INVITE_EMAIL_TEMPLATE } from '@frontend/app/containers/Projects/SettingsPage/Recruitment/InviteEmailTemplate/DefaultInviteEmailTemplate';
import { SectionCard } from '@frontend/app/containers/Projects/CampaignPage/components/SectionCard';
import { Button } from '@frontend/shadcn/components/ui/button';
import { P } from '@frontend/shadcn/components/typography/p';
import { Badge } from '@frontend/shadcn/components/ui/badge';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { MessageTemplatesQuery_templates } from '@frontend/app/queries/types/MessageTemplatesQuery';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Empty } from '@frontend/app/refresh-components';
import { EnvelopeIcon, PenIcon, PlusIcon } from '@revfluence/fresh-icons/solid/esm';
import { EmailTemplateDrawer } from '@frontend/app/containers/Projects/SettingsPage/components/EmailTemplateDrawer';

interface EmailTemplatesContainerProps {
  programId?: number;
}

export const EmailTemplatesContainer: React.FC<EmailTemplatesContainerProps> = ({ programId }) => {
  const { data: project } = useProjectByIdQuery({
    variables: {
      id: programId || 0,
    },
    fetchPolicy: 'cache-first',
    skip: !programId, // Skip query if programId is null
  });

  const { clientInfo } = useAuth();

  const { defaultMemberFieldSchemas } = useGetDefaultMemberFieldSchemasQuery();

  const [saveProgram] = useSaveProgramMutation(project?.project?.communityId);

  const { showErrorMessage, showInfoMessage } = useMessagingContext();

  const firstNameSchema = defaultMemberFieldSchemas?.find((schema) => schema.name === 'First Name');

  const defaultEmailTitle = project?.project ? template(DEFAULT_INVITE_EMAIL_TEMPLATE.title)({ program: project.project }) : '';
  const defaultEmailSubject = clientInfo ? template(DEFAULT_INVITE_EMAIL_TEMPLATE.subject)({ client: clientInfo }) : '';
  const defaultEmailText = firstNameSchema ? template(DEFAULT_INVITE_EMAIL_TEMPLATE.text)({ id: firstNameSchema.id }) : '';

  const handleMakeDefault = async (templateId: number) => {
    if (!programId) {
      showInfoMessage('Please attach this offer to a program first before setting email templates.');
      return;
    }

    await saveProgram({
      variables: {
        program: { id: programId, emailTemplateId: templateId },
      },
      onError: (error) => {
        logger.error('EmailTemplatesContainer.handleMakeDefault', error);
        showErrorMessage(error.message || 'Failed to make template default');
      },
    });
  };

  // Custom email templates implementation with programId check
  const { templates, loading: messageTemplatesLoading, refetch } = useGetMessageTemplates();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplatesQuery_templates | null>(null);

  const filteredEmailTemplates = useMemo(() => {
    if (messageTemplatesLoading || !programId) return [];
    return templates.filter(
      (template) =>
        template.type === TemplateType.INVITE &&
        template.programId === programId &&
        template.transportType === TemplateTransportType.EMAIL,
    );
  }, [messageTemplatesLoading, templates, programId]);

  const loading = messageTemplatesLoading;

  const handleEditClick = (template: MessageTemplatesQuery_templates) => {
    if (!programId) {
      showInfoMessage('Please attach this offer to a program first before setting up email templates.');
      return;
    }
    setSelectedTemplate(template);
    setIsDrawerOpen(true);
  };

  const handleCreateClick = () => {
    if (!programId) {
      showInfoMessage('Please attach this offer to a program first before setting up email templates.');
      return;
    }
    setSelectedTemplate(null);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedTemplate(null);
  };

  const handleMutationSuccess = () => {
    refetch();
    handleDrawerClose();
  };

  return (
    <>
      <SectionCard
        title="Invite Email Template"
        description="Set and customize your email template for all creators that are sent an invitation to apply."
        rightContent={
          <Button variant="outline" size="xs" onClick={handleCreateClick} className="self-start mt-0.5">
            <PlusIcon className="mr-1.5" />
            Add Email Template
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          {loading && <Skeleton data-testid="skeleton" className="h-10 w-full" />}
          {!loading && filteredEmailTemplates.length === 0 && (
            <Empty
              icon={EnvelopeIcon}
              title="No email templates found"
              description="Create email templates to send to the creators."
              primaryCtaProps={{
                text: 'Create Template',
                onClick: handleCreateClick,
              }}
              layout="horizontal"
              size="lg"
              withBackground
            />
          )}
          {!loading && filteredEmailTemplates.map((template) => (
            <Card key={template.id} className="rounded-2xl border border-border">
              <CardContent className="px-4 py-2 flex items-center justify-between">
                <P className="mb-0" fontWeight="font-medium">{template.title}</P>
                <div className="flex items-center gap-2">
                  {project?.project?.emailTemplateId === template.id && (
                    <Badge variant="secondary">Default</Badge>
                  )}
                  <Button
                    data-testid="edit-button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEditClick(template)}
                  >
                    <PenIcon />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </SectionCard>
      {programId && (
        <EmailTemplateDrawer
          open={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          template={selectedTemplate}
          projectId={programId}
          onSaveSuccess={handleMutationSuccess}
          onCancel={handleDrawerClose}
          onDeleteSuccess={handleMutationSuccess}
          defaultTemplateId={project?.project?.emailTemplateId}
          defaultEmailTitle={defaultEmailTitle}
          defaultEmailSubject={defaultEmailSubject}
          defaultEmailText={defaultEmailText}
          templateType={TemplateType.INVITE}
          handleMakeDefault={handleMakeDefault}
        />
      )}
    </>
  );
};
